import React, { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchJobLists,
  tagJob,
  tagReset,
} from "@workspace/job/src/store/actions";
import { useNavigate } from "react-router-dom";
import { DynamicTableHelper, useTableHook } from "@workspace/common";
import { Button, Offcanvas, Spinner } from "reactstrap";
import DynamicTableWrapper from "./DynamicTableWrapper";
import {
  JOB_MANDATORY_OPTIONS,
  JOB_INITIAL_OPTIONS,
} from "./CandidateTaggingConstants";
import "./CandidateTagging.scss";

function TagCandidate({ data, jobId, candidateId }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const jobTagMeta = useSelector((state) => state.JobStageReducer.jobTagMeta);
  const [loading, setLoading] = useState(false);

  const handleTag = (candidateId) => {
    const payload = {
      jobId: jobId,
      jobStageId: 1,
      status: "COMPLETED",
      candidateId: candidateId,
      stepName: "Profile",
      subStepName: "Tag",
    };
    try {
      setLoading(true);
      dispatch(tagJob({ payload: payload, navigate }));
      // Close Offcanvas
    } catch (e) {
      toast.error("Error tagging candidate.");
    }
  };

  useEffect(() => {
    if (jobTagMeta?.isSuccess) {
      dispatch(tagReset());
      setLoading(false);
    }
    if (jobTagMeta?.isError) {
      dispatch(tagReset());
      setLoading(false);
    }
  }, [jobTagMeta?.isSuccess, jobTagMeta?.isError]);

  return (
    <Button
      className="tag-cnd-btn"
      onClick={() => handleTag(candidateId)}
      disabled={loading}
    >
      {loading ? <Spinner size="sm" /> : "Tag Candidate"}
    </Button>
  );
}

function CandidateTagging({
  selectedCandidate,
  setCandidateTaggingOpen,
  candidateTaggingOpen,
}) {
  const dispatch = useDispatch();
  // Selector to retrieve all jobs
  const jobs = useSelector((state) => state.JobListReducer.jobs);
  const [gridView, setGridView] = useState("all_jobs");
  const [candidateId, setCandidateId] = useState(selectedCandidate?.id);
  const [tableConfig, setTableConfig] = useState(null);
  const candidateData = selectedCandidate?.candidateSubmissionData;
  const hasPageRendered = useRef(false);

  useEffect(() => {
    if (selectedCandidate != null) {
      setCandidateId(selectedCandidate?.id);
    }
  }, [selectedCandidate]);

  // Table Hooks
  const {
    pageRequest,
    pageRequestSet,
    pageInfo,
    setPageInfoData,
    search,
    setSearch,
    customConfig,
    setCustomConfigData,
    setTableData,
    tableData,
    handleRowCheck,
    selectAllRows,
    activeRow,
    setActiveRow,
  } = useTableHook(
    {
      page: 0,
      pageSize: 50,
      sortBy: null,
      sortDirection: "asc",
      searchTerm: null,
      searchFields: DynamicTableHelper.generateSeachFieldArray([]),
      candidateId: candidateId,
    },
    JOB_MANDATORY_OPTIONS,
    JOB_INITIAL_OPTIONS
  );

  useEffect(() => {
    const request = {
      ...pageRequest,
      jobType: gridView,
      candidateId: candidateId,
    };
    if (pageRequest?.searchFields?.length > 0 && candidateId !== null) {
      setActiveRow([]);
      dispatch(fetchJobLists(DynamicTableHelper.cleanPageRequest(request)));
    }
  }, [
    JSON.stringify({ ...pageRequest, jobType: gridView }),
    candidateId,
    gridView,
  ]);

  useEffect(() => {
    if (hasPageRendered.current) {
      pageRequestSet.setPage(0);
    }
    hasPageRendered.current = true;
  }, [gridView]);

  useEffect(() => {
    if (jobs) {
      setPageInfoData(jobs);
    }
    setTableData(jobs?.jobs);
  }, [jobs]);

  // Generate Job List Config
  const generateJobListConfig = (customConfig) => {
    return [
      {
        header: "#",
        name: "indexing",
        sort: false,
        render: (data, index) => (
          <div className="d-flex column-gap-2">
            {pageInfo?.currentPage * pageInfo?.pageSize + (index + 1)}.
          </div>
        ),
      },
      ...customConfig,
      {
        header: "Action",
        name: "action",
        sort: false,
        sortValue: "action",
        sticky: "right",
        expand: true,
        center: true,
        render: (data) => {
          return (
            <TagCandidate
              jobId={data.id}
              data={data}
              candidateId={candidateId}
            />
          );
        },
      },
    ];
  };

  useEffect(() => {
    if (tableConfig) {
      const newConfig = generateJobListConfig(customConfig);
      newConfig.forEach((item, index) => {
        const oldConfig = tableConfig?.find(
          (oldItem) => oldItem?.name === item?.name
        );
        // If cannot find then continue
        if (!oldConfig) return;
        if (oldConfig?.expand) {
          newConfig[index].expand = true;
        } else {
          newConfig[index].expand = false;
        }
      });
      setTableConfig(newConfig);
    } else {
      setTableConfig(generateJobListConfig(customConfig));
    }
  }, [customConfig, pageInfo, activeRow, tableData]);

  // Close Offcanvas
  const handleClose = () => setCandidateTaggingOpen(!candidateTaggingOpen);
  return (
    <React.Fragment>
      <Offcanvas
        isOpen={candidateTaggingOpen}
        toggle={handleClose}
        direction="end"
        style={{ width: "65vw" }}
      >
        <div className="offcanvas-header pe-0">
          <div className="d-flex flex-row align-items-start justify-content-between w-100">
            <div className="d-flex flex-column gap-2">
              <span className="fw-semibold fs-5">Tag Candidate to Job</span>
              <div className="d-flex flex-row gap-2 text-muted">
                <span>
                  {candidateData?.firstName} {candidateData?.lastName} |
                </span>
                <span>{candidateData?.email} |</span>
                <span>{candidateData?.phone} |</span>
                <span>{candidateData?.currentLocation}</span>
              </div>
            </div>
            <Button onClick={handleClose} className="cancel-tag-cnd-btn me-3">
              Cancel
            </Button>
          </div>
        </div>
        <div className="offcanvas-body">
          <DynamicTableWrapper
            data={jobs?.jobs ?? []}
            config={tableConfig}
            pageInfo={pageInfo}
            pageRequest={pageRequest}
            pageRequestSet={pageRequestSet}
            search={search}
            setSearch={setSearch}
            setCustomConfigData={setCustomConfigData}
            gridView={gridView}
            activeRow={activeRow}
            setActiveRow={setActiveRow}
            setTableConfig={setTableConfig}
          />
        </div>
      </Offcanvas>
    </React.Fragment>
  );
}

export default CandidateTagging;
